// @flow
import { combineReducers } from 'redux';
import { createReducer, AsyncRequest, analyticsActions } from 'angieslist-app-platform';
// $FlowFixMe
import { List } from 'immutable';
import imagesActionKeys from '../../../app/common/images/imagesActionKeys';
import { defaultProps } from '../../../app/browser/analyticsDefaultProperties';
import type { RemoveImagesAction, ShowConfirmRemoveImageAction } from '../../../app/common/images/imagesActions';
import type {
  AddImagesRequest,
  AddImagesResponse,
  RemoveImagesRequest,
  RemoveImagesResponse,
  ServiceProviderImage,
  UpdateImageRequest,
  UpdateImageResponse,
  SaveLogoRequest,
  SaveLogoResponse, GetPhotosRequest, GetPhotosResponse,
} from '../../../app/common/images/imagesService';

type AsyncState<Req, Res> = {|
  isLoading: boolean,
  request: ?Req,
  response: ?Res,
  error: ?Error,
|};

export type ImagesReducerState = {
  all: Array<ServiceProviderImage>,
  getPhotos: AsyncState<GetPhotosRequest, GetPhotosResponse>,
  addRequest: AsyncState<AddImagesRequest, AddImagesResponse>,
  updateImage: AsyncState<UpdateImageRequest, UpdateImageResponse>,
  removeImages: AsyncState<RemoveImagesRequest, RemoveImagesResponse>,
  showConfirmRemove: ?ServiceProviderImage,
  getCoverPhoto: AsyncState<any, any>,
  createCoverPhoto: AsyncState<any, any>,
  saveLogo: AsyncState<SaveLogoRequest, SaveLogoResponse>,
};

const all = createReducer([], {
  [imagesActionKeys.GET_PHOTOS_SUCCESS]:
    (state: List<ServiceProviderImage>, action: {
      payload: {
        body: GetPhotosResponse
      }
    }) => state.concat(action.payload.body.images),
  [imagesActionKeys.ADD_IMAGES_SUCCESS]:
    (state, action) => state.concat(action.payload),
  [imagesActionKeys.REMOVE_IMAGES_SUCCESS]:
    (state: List<ServiceProviderImage>, action: RemoveImagesAction) => {
      const { imageIds } = action.meta;
      return state.filter(value => !imageIds.includes(value.id));
    },
});

const getPhotos = AsyncRequest.createReducer(imagesActionKeys.GET_PHOTOS);

const getDefaultProps = action => ({
  ...defaultProps,
  activityLocation: 'SP : Photos',
  serviceProviderId: action.meta.serviceProviderId,
  fileId: action.meta.imageIds.join(),
});
const getUploadImageProps = action => ({
  ...defaultProps,
  activityLocation: 'SP : Photos',
  serviceProviderId: action.meta.serviceProviderId,
  description: 'SP successfully added photo',
});
const getUpdateImageProps = action => ({
  ...defaultProps,
  activityLocation: 'SP : Photos',
  serviceProviderId: action.meta.serviceProviderId,
  description: 'SP successfully updated photo',
});

const addRequest = AsyncRequest.createReducer(imagesActionKeys.ADD_IMAGES, {
  [imagesActionKeys.ADD_IMAGES_ERROR]:
    (state, action) => {
      action.addSideEffect(({ dispatch }) => {
        dispatch(analyticsActions.sendEnhancedTrackEvent({
          name: 'Error Fired',
          properties: {
            ...defaultProps,
            activityLocation: 'SP : Photos',
            serviceProviderId: action.meta.serviceProviderId,
            description: 'File Other',
          },
        }));
      });
      return AsyncRequest.setError(state, action.payload);
    },
  [imagesActionKeys.ADD_IMAGES_SUCCESS]: (state, action) => {
    action.addSideEffect(({ dispatch }) => {
      dispatch(analyticsActions.sendEnhancedTrackEvent({
        name: 'Item Clicked',
        properties: {
          ...getUploadImageProps(action),
        },
      }));
    });
    return AsyncRequest.setSuccess(state, action.payload);
  },
});

const updateImage = AsyncRequest.createReducer(imagesActionKeys.UPDATE_IMAGES, {
  [imagesActionKeys.UPDATE_IMAGES_SUCCESS]: (state, action) => {
    action.addSideEffect(({ dispatch }) => {
      dispatch(analyticsActions.sendEnhancedTrackEvent({
        name: 'Item Clicked',
        properties: {
          ...getUpdateImageProps(action),
        },
      }));
    });
    location.reload() // eslint-disable-line
    return AsyncRequest.setSuccess(state, action.payload);
  },
  [imagesActionKeys.UPDATE_IMAGES_RESET]: (state) => {
    location.reload() // eslint-disable-line
    return AsyncRequest.setSuccess(state);
  },
});

const removeImages = AsyncRequest.createReducer(imagesActionKeys.REMOVE_IMAGES, {
  [imagesActionKeys.REMOVE_IMAGES_ERROR]: (state, action) => {
    action.addSideEffect(({ dispatch }) => {
      dispatch(analyticsActions.sendEnhancedTrackEvent({
        name: 'Error Fired',
        properties: {
          ...getDefaultProps(action),
          description: 'Remove Image Failed',
        },
      }));
    });
    return AsyncRequest.setError(state, action.payload);
  },
  [imagesActionKeys.REMOVE_IMAGES_SUCCESS]: (state, action) => {
    action.addSideEffect(({ dispatch }) => {
      dispatch(analyticsActions.sendEnhancedTrackEvent({
        name: 'Image Removed',
        properties: {
          ...getDefaultProps(action),
        },
      }));
    });
    return AsyncRequest.setSuccess(state, action.meta);
  },
});

const showConfirmRemove = createReducer(null, {
  [imagesActionKeys.HIDE_CONFIRM_REMOVE_IMAGE]: () => null,
  [imagesActionKeys.SHOW_CONFIRM_REMOVE_IMAGE]:
    (state: ?ServiceProviderImage, action: ShowConfirmRemoveImageAction) => action.payload,
  [imagesActionKeys.REMOVE_IMAGES_START]: () => null,
});

const getCoverPhoto = AsyncRequest.createReducer(imagesActionKeys.GET_COVER_PHOTO);
const createCoverPhoto = AsyncRequest.createReducer(imagesActionKeys.CREATE_COVER_PHOTO, {
  [imagesActionKeys.CREATE_COVER_PHOTO_ERROR]: (state, action) => {
    action.addSideEffect(({ dispatch }) => {
      dispatch(analyticsActions.sendEnhancedTrackEvent({
        name: 'Profile Builder : Photo Error State',
        properties: {
          ...defaultProps,
          activityLocation: 'Profile Builder : Photo',
          serviceProviderId: action.meta.serviceProviderId,
        },
      }));
    });
    return AsyncRequest.setSuccess(state, action.payload);
  },
  [imagesActionKeys.CREATE_COVER_PHOTO_SUCCESS]: (state, action) => {
    action.addSideEffect(({ dispatch }) => {
      dispatch(analyticsActions.sendEnhancedTrackEvent({
        name: 'Item Clicked',
        properties: {
          ...defaultProps,
          activityLocation: 'Profile Builder : Photo',
          serviceProviderId: action.meta.serviceProviderId,
          description: 'SP successfully added profile photo',
        },
      }));
    });
    return AsyncRequest.setSuccess(state, action.payload);
  },
});
const saveLogo = AsyncRequest.createReducer(imagesActionKeys.SAVE_LOGO);

export default (combineReducers({
  all,
  getPhotos,
  addRequest,
  updateImage,
  removeImages,
  showConfirmRemove,
  getCoverPhoto,
  createCoverPhoto,
  saveLogo,
}): any);



// WEBPACK FOOTER //
// ./src/modules/store/common/imagesReducer.js